import { useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Button } from "@/components/design/Button";
import { userErrorMessage } from "@/lib/user-error";
import { OwnerAccessRow, PersonAdder } from "@/components/match/MatchModals";
import { PROJECT_STATUSES, PROJECT_STATUS_LABELS, type PlanPerson, type ProjectStatus } from "./project-plan";
import { PROJECT_ROLE_LABELS, type ProjectRole } from "./project-access";

// Die Stammdaten des Projekts an einer Stelle, erreichbar über das Zahnrad -
// dieselbe Geste und dieselbe Gestalt wie im Match (MatchSettingsModal): breiter
// Kasten, Eyebrow über der Überschrift, scrollender Rumpf, Fusszeile mit
// Abbrechen und Speichern.
//
// Der Personensucher ist WÖRTLICH derselbe Baustein (PersonAdder): tippen, die
// Treffer stehen darunter mit Beruf, ein Klick fügt hinzu. Ein Auswahlfeld an
// seiner Stelle zwang dazu, erst aufzuklappen und dann zu suchen, und es zeigte
// nur die eigenen Kontakte - wer jemanden hinzufügt, meint aber oft jemanden,
// den er gerade erst kennengelernt hat.
//
// Dass Titel und Beschreibung auch im Kopf editierbar sind, ist keine Dopplung,
// sondern zwei Wege zum selben Wert: im Kopf ändert man im Vorbeigehen, hier
// setzt man sich hin. Das Match hält es genauso.
//
// Der ZUGRIFF ist die eigentliche Arbeit dieses Dialogs: wer das Projekt
// verwalten oder sehen darf. Das hat mit den Kandidaten nichts zu tun - ein
// Kandidat ist jemand, den man FÜR das Projekt gewinnen will, und er sieht es
// nicht einmal.

export interface ProjectAccessDraft {
  person: PlanPerson;
  role: ProjectRole;
}

export function ProjectSettingsModal({
  title,
  description,
  status,
  access,
  owner,
  canEdit,
  onSave,
  onClose,
}: {
  title: string;
  description: string;
  status: ProjectStatus;
  /** Die aktuellen Zugriffszeilen, OHNE den Ersteller. */
  access: readonly ProjectAccessDraft[];
  /** Der Ersteller; steht als unveränderliche Zeile obenan. */
  owner: { id?: string | null; name: string } | null;
  /** Ohne Schreibrecht nur lesen, wie im Match (`Project.actions.canManageAccess`). */
  canEdit: boolean;
  onSave: (next: {
    title: string;
    description: string;
    status: ProjectStatus;
    access: readonly ProjectAccessDraft[];
  }) => Promise<void>;
  onClose: () => void;
}) {
  const intl = useIntl();
  const [draftTitle, setDraftTitle] = useState(title);
  const [draftDescription, setDraftDescription] = useState(description);
  const [draftStatus, setDraftStatus] = useState<ProjectStatus>(status);
  const [draftAccess, setDraftAccess] = useState<readonly ProjectAccessDraft[]>(access);
  // Gespeichert wird wie im Match (MatchSettingsModal.save): erst wenn genesis zugestimmt hat,
  // schließt der Dialog; ein Fehler bleibt im Dialog stehen, statt als Erfolg durchzugehen (spark#1001).
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    if (saving || !canEdit) return;
    setSaving(true);
    setError(null);
    try {
      await onSave({
        title: draftTitle.trim(),
        description: draftDescription,
        status: draftStatus,
        access: draftAccess,
      });
      onClose();
    } catch (e) {
      setError(userErrorMessage(intl, e));
    } finally {
      setSaving(false);
    }
  }

  const excludeIds = useMemo(
    () => [owner?.id, ...draftAccess.map((row) => row.person.id)].filter((s): s is string => Boolean(s)),
    [draftAccess, owner?.id],
  );

  return (
    <div
      id="project-settings-modal"
      className="fixed inset-0 z-[var(--z-modal)] flex items-center justify-center p-6"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-ink/40 backdrop-blur-sm" />
      <div
        className="box relative flex max-h-[85vh] w-full max-w-2xl flex-col overflow-hidden bg-surface"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-b border-ink/5 px-6 py-4">
          <div className="text-smycles-micro tracking-[0.25em] text-ink-muted">
            <FormattedMessage id="project.settings.title" defaultMessage="Projekt-Einstellungen" />
          </div>
          <div className="mt-0.5 font-serif text-smycles-display-s">
            <FormattedMessage id="project.settings.heading" defaultMessage="Name, Status & Zugriff" />
          </div>
        </div>

        <div className="flex-1 space-y-5 overflow-auto px-6 py-5">
          {!canEdit && (
            <div className="bg-ink/[0.06] rounded-md px-3 py-2 text-smycles-meta text-ink-muted">
              <FormattedMessage
                id="project.settings.noWriteRights"
                defaultMessage="Du hast keine Schreibrechte auf diese Projekt-Einstellungen."
              />
            </div>
          )}

          <div>
            <div className="mb-1 text-smycles-micro tracking-[0.25em] text-ink-muted">
              <FormattedMessage id="project.create.titleLabel" defaultMessage="Titel" />
            </div>
            <input
              id="project-settings-name"
              value={draftTitle}
              onChange={(e) => setDraftTitle(e.target.value)}
              disabled={!canEdit}
              className="bg-page w-full rounded-md px-3 py-2 text-smycles-body ring-1 ring-ink/10 focus:outline-none focus:ring-ink-ui disabled:opacity-60"
            />
          </div>

          <div>
            <div className="mb-1 text-smycles-micro tracking-[0.25em] text-ink-muted">
              <FormattedMessage id="project.workspace.description" defaultMessage="Beschreibung" />
            </div>
            <textarea
              id="project-settings-description"
              value={draftDescription}
              onChange={(e) => setDraftDescription(e.target.value)}
              disabled={!canEdit}
              rows={3}
              className="bg-page w-full resize-none rounded-md px-3 py-2 text-smycles-body leading-relaxed ring-1 ring-ink/10 focus:outline-none focus:ring-ink-ui disabled:opacity-60"
            />
          </div>

          <div>
            <div className="mb-1 text-smycles-micro tracking-[0.25em] text-ink-muted">
              <FormattedMessage id="project.meta.status" defaultMessage="Status" />
            </div>
            <select
              id="project-settings-status"
              value={draftStatus}
              onChange={(e) => setDraftStatus(e.target.value as ProjectStatus)}
              disabled={!canEdit}
              className="bg-page w-full rounded-md px-3 py-2 text-smycles-body ring-1 ring-ink/10 focus:outline-none focus:ring-ink-ui disabled:opacity-60"
            >
              {PROJECT_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {intl.formatMessage(PROJECT_STATUS_LABELS[s])}
                </option>
              ))}
            </select>
          </div>

          <div>
            <div className="mb-2 text-smycles-micro tracking-[0.25em] text-ink-muted">
              <FormattedMessage
                id="project.access.count"
                defaultMessage="Zugriff · {count}"
                values={{ count: draftAccess.length + (owner ? 1 : 0) }}
              />
            </div>
            <p className="smycles-field-meta mb-2 text-ink-muted">
              <FormattedMessage
                id="project.access.hint"
                defaultMessage="Wer hier steht, sieht das Projekt. Kandidaten sind etwas anderes: sie werden gefragt und haben keinen Zugriff."
              />
            </p>

            <div className="bg-page box-flat divide-y divide-ink/5 px-4">
              {owner ? <OwnerAccessRow name={owner.name || "–"} /> : null}

              {draftAccess.map((row) => (
                <div key={row.person.id} id={`project-access-row-${row.person.id}`} className="flex items-center gap-3 py-2">
                  <div className="min-w-0 flex-1 truncate text-smycles-meta font-medium">{row.person.name || "–"}</div>
                  <select
                    id={`project-access-role-${row.person.id}`}
                    value={row.role}
                    disabled={!canEdit}
                    onChange={(e) =>
                      setDraftAccess((cur) =>
                        cur.map((r) =>
                          r.person.id === row.person.id ? { ...r, role: e.target.value as ProjectRole } : r,
                        ),
                      )
                    }
                    className="bg-page rounded px-2 py-1 text-smycles-meta ring-1 ring-ink/10 focus:outline-none focus:ring-ink-ui disabled:opacity-60"
                  >
                    {(Object.keys(PROJECT_ROLE_LABELS) as ProjectRole[]).map((role) => (
                      <option key={role} value={role}>
                        {intl.formatMessage(PROJECT_ROLE_LABELS[role])}
                      </option>
                    ))}
                  </select>
                  {canEdit && (
                    <Button
                      id={`project-access-remove-${row.person.id}`}
                      variant="icon"
                      size="sm"
                      onClick={() => setDraftAccess((cur) => cur.filter((r) => r.person.id !== row.person.id))}
                      title={intl.formatMessage({ id: "common.remove", defaultMessage: "Entfernen" })}
                      aria-label={intl.formatMessage({ id: "common.remove", defaultMessage: "Entfernen" })}
                    >
                      ✕
                    </Button>
                  )}
                </div>
              ))}

              {draftAccess.length === 0 && !owner ? (
                <div className="py-6 text-center text-smycles-meta text-ink-muted">
                  <FormattedMessage
                    id="project.access.none"
                    defaultMessage="Niemand hat Zugriff. Unten Person hinzufügen."
                  />
                </div>
              ) : null}
            </div>

            {canEdit && (
              <div className="mt-3">
                <PersonAdder
                  idPrefix="project-settings-access"
                  excludeIds={excludeIds}
                  onAdd={(p) =>
                    setDraftAccess((cur) => [
                      ...cur,
                      { person: { id: p.id, name: p.formattedName }, role: "reader" },
                    ])
                  }
                />
              </div>
            )}
          </div>
        </div>

        {error && (
          <div role="alert" className="mx-6 mb-3 rounded-md bg-red-50 px-3 py-2 text-smycles-meta text-red-700">
            {error}
          </div>
        )}

        <div className="flex shrink-0 justify-end gap-2 border-t border-ink/5 px-6 py-4">
          <Button id="project-settings-cancel" variant="secondary" size="compact" onClick={onClose} disabled={saving}>
            {canEdit ? (
              <FormattedMessage id="common.cancel" defaultMessage="Abbrechen" />
            ) : (
              <FormattedMessage id="common.close" defaultMessage="Schließen" />
            )}
          </Button>
          {canEdit && (
            <Button
              id="project-settings-save"
              variant="primary"
              size="compact"
              onClick={() => void save()}
              disabled={saving}
            >
              {saving ? (
                <FormattedMessage id="common.saving" defaultMessage="Speichern…" />
              ) : (
                <FormattedMessage id="common.save" defaultMessage="Speichern" />
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
